import { useGLTF } from "@react-three/drei";
import { useConfigurationOptionsContext } from "app/providers/DoorOptionsProvider";
import { getFullAssetPath } from "shared/utils/assets";
import {
  defaultDoorHeight,
  defaultDoorWidth,
} from "shared/utils/doorSceneConstants";
import { GLTFMaterialResult } from "shared/utils/door";
import * as THREE from "three";
import { GLTF } from "three-stdlib";
import { lerp } from "three/src/math/MathUtils";

type GLTFResult = GLTF & {
  nodes: {
    kozijn_top: THREE.Mesh;
    kozijn_left: THREE.Mesh;
    kozijn_right: THREE.Mesh;
  };
  materials: {};
};

export function KaderdeurKozijn() {
  const { nodes } = useGLTF(
    getFullAssetPath("/objects-v3/kaderdeurKozijn.glb"),
  ) as GLTFResult;

  const { materials } = useGLTF(
    getFullAssetPath("/objects-v3/materialsCompressedv4.glb"),
  ) as GLTFMaterialResult;

  const {
    getConfigurationHeight,
    getConfigurationWidth,
    getConfigurationColor,
    minDoorHeight,
    maxDoorHeight,
  } = useConfigurationOptionsContext();

  const selectedMaterial =
    materials[getConfigurationColor() as keyof typeof materials];

  const targetHeight = getConfigurationHeight(0);
  const heightScaleFactor = targetHeight / defaultDoorHeight;

  const targetWidth = getConfigurationWidth(0);
  const widthScaleFactor = targetWidth / defaultDoorWidth;

  // Width position changes
  const leftPositionX = (targetWidth - defaultDoorWidth) / 2 / 1000; // Move left kozijn to the left edge
  const rightPositionX = -(targetWidth - defaultDoorWidth) / 2 / 1000;

  // Height adjustment
  const clampedHeight = Math.min(
    Math.max(targetHeight, minDoorHeight),
    maxDoorHeight,
  );
  const interpolatedFactor = lerp(
    -0.01,
    +0.005,
    (clampedHeight - minDoorHeight) / (maxDoorHeight - minDoorHeight),
  );

  // Height positiion changes
  const topPositionY =
    (targetHeight - defaultDoorHeight) / 1000 + interpolatedFactor; // Move top kozijn to the top edge

  return (
    <group dispose={null}>
      <mesh
        castShadow
        receiveShadow
        geometry={nodes.kozijn_top.geometry}
        material={selectedMaterial}
        scale-x={widthScaleFactor}
        position-y={topPositionY}
      />
      <mesh
        castShadow
        receiveShadow
        geometry={nodes.kozijn_left.geometry}
        material={selectedMaterial}
        scale-y={heightScaleFactor}
        position-x={leftPositionX} // Adjust left kozijn position
      />
      <mesh
        castShadow
        receiveShadow
        geometry={nodes.kozijn_right.geometry}
        material={selectedMaterial}
        scale-y={heightScaleFactor}
        position-x={rightPositionX} // Adjust right kozijn position
      />
    </group>
  );
}
